import * as cornerstone from "@cornerstonejs/core";
import hardcodedMetaDataProvider from "./MetadataProvider";

const { calibratedPixelSpacingMetadataProvider } = cornerstone.utilities;

// Priority for the USER calibration store that `calibrateImageSpacing()` writes
// into (see CalibrationService.applyCalibration). Cornerstone ships this
// provider but never registers it with metaData — apps are expected to do it —
// so without this registration `metaData.get("calibratedPixelSpacing", …)`
// inside StackViewport always returns undefined and a manual calibration is
// silently dropped. Kept above every other provider so a user calibration wins.
const USER_CALIBRATION_PRIORITY = 20000;

// Priority for the spacing-label fallback below. Deliberately lower than
// USER_CALIBRATION_PRIORITY (and than the contextual/hardcoded providers) so it
// only answers when nothing else did.
const DEFAULT_SPACING_CALIBRATION_PRIORITY = 900;

// One shared calibration object for every image that needs the fallback.
// StackViewport's calibrateIfNecessary() compares the calibration by IDENTITY to
// decide whether to re-publish IMAGE_SPACING_CALIBRATED, so a per-image literal
// would fire that event on every frame change and thrash annotation stats during
// cine playback. The payload carries no per-image data, so one instance is safe.
const DEFAULT_SPACING_CALIBRATION = { scale: 1 };

// Per-imageId decision cache: DEFAULT_SPACING_CALIBRATION or undefined
// ("this image does not need the fallback").
const defaultSpacingCalibrations = new Map();

/**
 * True when the instance carries Sequence of Ultrasound Regions (0018,6011)
 * with at least one region. Those images can already measure in real physical
 * units (cm, via physicalDeltaX/Y) inside getCalibratedLengthUnitsAndScale, so
 * a *substituted* 1 px = 1 mm spacing must not be forced on them.
 */
const hasUltrasoundRegions = (imageId) => {
  try {
    const calibrationModule = cornerstone.metaData.get(
      "calibrationModule",
      imageId
    );
    const regions = calibrationModule?.sequenceOfUltrasoundRegions;
    return Array.isArray(regions) && regions.length > 0;
  } catch (_) {
    return false;
  }
};

/**
 * Makes measurements read in mm on instances whose spacing the unit-label logic
 * refuses to trust.
 *
 * getCalibratedUnits derives "mm" vs "px" from `image.hasPixelSpacing` alone, and
 * StackViewport derives that from `imagePlaneModule.usingDefaultValues`. The DICOM
 * loaders raise that flag in two unrelated situations, and only the first is
 * actually about spacing:
 *
 *   1. Pixel Spacing (0028,0030) was absent, so rowPixelSpacing =
 *      columnPixelSpacing = 1 was substituted — the "defaults to 1" behaviour the
 *      dicom-image-loader patch builds on. The substituted 1 is used for the
 *      arithmetic while the label stays "px", so defaulting to 1 can never on its
 *      own flip the unit.
 *   2. (wadors path only) ImageOrientationPatient / ImagePositionPatient were
 *      absent — the norm for US, which sends neither. The flag is raised even
 *      when the instance carries perfectly good spacing, so US measures in "px"
 *      despite real Pixel Spacing or Imager Pixel Spacing.
 *
 * Returning a calibration with `scale: 1` flips `hasPixelSpacing` to true without
 * altering any distance: lengths are computed as `indexDistance / scale` where
 * `scale = 1 / spacing`, so the image's own spacing still applies — real spacing
 * where the DICOM had it, the substituted 1 mm where it did not.
 *
 * `imagePlaneModule.pixelSpacing` is the RAW extracted array (undefined when the
 * loader had to substitute), which tells the two situations apart even though
 * `usingDefaultValues` lumps them together:
 *
 *   - pixelSpacing present → real spacing (0028,0030, or Imager Pixel Spacing
 *     0018,1164 via the dicom-image-loader patch) being ignored only because US
 *     carries no orientation/position. Always relabel: the mm value is genuine.
 *     A matching ultrasound region still reports cm, because that branch of
 *     getCalibratedUnits overrides `unit` regardless of hasPixelSpacing.
 *   - pixelSpacing absent → spacing was substituted with 1. Relabel only when
 *     there is no ultrasound region data to measure with instead.
 */
const defaultSpacingCalibrationProvider = (type, imageId) => {
  if (type !== "calibratedPixelSpacing") return undefined;
  if (typeof imageId !== "string") return undefined;

  if (defaultSpacingCalibrations.has(imageId)) {
    return defaultSpacingCalibrations.get(imageId);
  }

  const imagePlaneModule = cornerstone.metaData.get(
    "imagePlaneModule",
    imageId
  );
  // Metadata not fetched yet — answer nothing and stay uncached so the next
  // lookup (after the /metadata or DICOM P10 fetch lands) can decide properly.
  if (!imagePlaneModule) return undefined;

  const {
    usingDefaultValues,
    rowPixelSpacing,
    columnPixelSpacing,
    pixelSpacing,
  } = imagePlaneModule;

  const hasRealSpacing =
    Array.isArray(pixelSpacing) &&
    pixelSpacing.length > 0 &&
    pixelSpacing.every((value) => value > 0);

  const needsFallback =
    usingDefaultValues === true &&
    rowPixelSpacing > 0 &&
    columnPixelSpacing > 0 &&
    (hasRealSpacing || !hasUltrasoundRegions(imageId));

  const calibration = needsFallback ? DEFAULT_SPACING_CALIBRATION : undefined;
  defaultSpacingCalibrations.set(imageId, calibration);
  return calibration;
};

let calibrationProvidersRegistered = false;

/**
 * Wires up the two providers that answer `calibratedPixelSpacing`: the USER
 * calibration store (so CalibrationService's manual calibration takes effect)
 * and the spacing-label fallback above.
 *
 * Idempotent, and called unconditionally from CornerstoneService.initialize() as
 * well as from registerGlobalMetadataProvider() — the Gizmo and AdvanceViewer
 * entry points call cornerstone.init() on their own, which makes initialize()
 * skip its one-time block entirely.
 */
export const registerCalibrationMetadataProviders = () => {
  if (calibrationProvidersRegistered) return;
  calibrationProvidersRegistered = true;

  // Wrapped rather than passed raw: the provider runs imageIdToURI() on its
  // argument, and cornerstone fires metaData.get with an undefined imageId
  // during some internal lookups (see MetadataProvider.jsx). getMetaData() does
  // not catch provider throws, so an unguarded highest-priority provider would
  // break those lookups.
  cornerstone.metaData.addProvider(
    (type, imageId) =>
      typeof imageId === "string"
        ? calibratedPixelSpacingMetadataProvider.get(type, imageId)
        : undefined,
    USER_CALIBRATION_PRIORITY
  );
  cornerstone.metaData.addProvider(
    defaultSpacingCalibrationProvider,
    DEFAULT_SPACING_CALIBRATION_PRIORITY
  );
};

/**
 * Global metadata provider registration
 * Called once during Cornerstone initialization to register the default metadata provider
 * This prevents duplicate provider registrations and ensures consistent metadata handling
 */
export const registerGlobalMetadataProvider = () => {
  // Priority 1000: Lower priority for fallback hardcoded provider
  // This allows higher priority providers to take precedence
  cornerstone.metaData.addProvider(
    (type, imageId, imageIds) =>
      hardcodedMetaDataProvider(type, imageId, imageIds),
    1000
  );

  registerCalibrationMetadataProviders();
};

/**
 * Register contextual metadata provider for specific image list
 * Use this when you need context-specific metadata (e.g., for a specific viewport)
 * Higher priority ensures this is checked before the global provider
 */
export const addContextualMetadataProvider = (imageIds) => {
  cornerstone.metaData.addProvider(
    (type, imageId) =>
      hardcodedMetaDataProvider(type, imageId, imageIds),
    5000  // Higher priority for contextual data
  );
};
